import { getBscGlobalState, sendPackageOperationalEvent } from '../../../../common/utils';
import { getItemCategory, isProcessingAllowedByPreferences } from '../../../../common/utils/item-processing';
import { Logger } from '../../../../common/utils/logger';
import { createIDBOpenDBRequestWrapper } from '../../../../services/indexed-db-wrapper-service/idb-open-request-wrapper';
import { isValidDBName } from '../../../../services/indexed-db-wrapper-service/utils';
import { ItemKeyCache } from '../../../../services/item-key-cache-service';
import { CategoryStorageType, PreferenceCategory } from '../../../../types';

interface DatabaseEventDetails {
	databaseName: string;
}

// DatabaseBlockedError extends DOMException in browser environments, but DOMException is not
// available in SSR/Node.js, which causes import errors. We conditionally extend DOMException
// only when it is available, falling back to a plain Error in SSR — the same pattern used
// for IDBOpenDBRequestWrapper.
let DatabaseBlockedError: new (message?: string, name?: string) => Error;

if (typeof DOMException !== 'undefined') {
	DatabaseBlockedError = class DatabaseBlockedError extends DOMException {};
} else {
	DatabaseBlockedError = class DatabaseBlockedError extends Error {
		constructor(message?: string, public name: string = 'DatabaseBlockedError') {
			super(message);
		}
	};
}

const checkAndSendAnalyticsEvent = async (eventDetails: DatabaseEventDetails): Promise<void> => {
	const wasSetByPackage = ItemKeyCache.wasSetByPackage(
		CategoryStorageType.IndexedDB,
		eventDetails.databaseName,
	);

	const productName = getBscGlobalState().product;

	const itemCategory = await getItemCategory(
		eventDetails.databaseName,
		CategoryStorageType.IndexedDB,
		productName,
	);

	const isStrictlyNecessary = itemCategory === PreferenceCategory.StrictlyNecessary;

	if (!isStrictlyNecessary && !wasSetByPackage) {
		const attributes = {
			...eventDetails,
			productName,
			outsidePackage: true,
		};

		// Send analytics event for uncategorized IndexedDB usage
		sendPackageOperationalEvent({
			action: 'usedAtlIndexedDBOpen',
			attributes,
		});
	}
};

const checkIfAllowed = async (dbName: string): Promise<boolean> => {
	if (!isValidDBName(dbName)) {
		const error = new Error('Invalid database name: must be a non-empty string');
		Logger.errorWithOperationalEvent({
			action: 'usedAtlIndexedDBOpenError',
			attributes: { dbName },
			message: `IndexedDB open failed: ${error.message}`,
		});
		throw error;
	}

	const productName = getBscGlobalState().product;

	return await isProcessingAllowedByPreferences({
		itemKey: dbName,
		storageType: CategoryStorageType.IndexedDB,
		productName,
	});
}


/**
 * @private
 * @deprecated
 */
export const getIndexedDBOpenOverrideEventOnly =
	(originalOpen: (name: string, version?: number) => IDBOpenDBRequest) => 
	(databaseName: string, version?: number): IDBOpenDBRequest => {
		checkAndSendAnalyticsEvent({
			databaseName,
		}).catch((e) => {
			Logger.error(`Failed to send IndexedDB open event. ${e.message || ''}`);
		});

		// Always allow the operation to proceed (we're only tracking usage)
		return originalOpen(databaseName, version);
	};

export const getIndexedDBOpenOverride =
	(originalOpen: (name: string, version?: number) => IDBOpenDBRequest) =>
	(databaseName: string, version?: number): IDBOpenDBRequest => {
		const wasSetByPackage = ItemKeyCache.wasSetByPackage(CategoryStorageType.IndexedDB, databaseName);
		if (wasSetByPackage) {
			return originalOpen(databaseName, version);
		}

		Logger.debug(`IndexedDB operation: overridden open on database \"${databaseName}\"`, { version: String(version ?? 'unspecified') });
		const requestWrapper = createIDBOpenDBRequestWrapper();

		checkIfAllowed(databaseName)
			.then((isAllowed) => {
				sendPackageOperationalEvent({
					action: 'usedAtlIndexedDBOpen',
					attributes: {
						databaseName,
						wasRejected: !isAllowed,
						outsidePackage: true,
					},
				});
	
				if(isAllowed) {
					Logger.debug(`IndexedDB operation: override on database \"${databaseName}\" was ALLOWED`, { version: String(version ?? 'unspecified')});

					const actualRequest = originalOpen(databaseName, version);

					requestWrapper.wrapRequest(actualRequest);
				} else {
					Logger.debug(`IndexedDB operation: override on database \"${databaseName}\" was REJECTED`, { version: String(version ?? 'unspecified')});

					const error = new DatabaseBlockedError('The database was blocked by consent preferences', 'storageItemBlocked');
					const errorEvent = new ErrorEvent('error', { message: error.message, error })
					requestWrapper.setError(error as unknown as DOMException);
					requestWrapper.dispatchEvent(errorEvent);
				}
			})
			.catch((e) => {
				const errorMessage = e instanceof Error ? e.message : String(e);
	
				Logger.debug(`IndexedDB operation: override on database \"${databaseName}\" ERROR: ${errorMessage}`, { version: String(version ?? 'unspecified')});

				Logger.errorWithOperationalEvent({
					action: 'usedAtlIndexedDBOpenError',
					attributes: {
						databaseName,
						outsidePackage: true,
					},
					message: `IndexedDB open failed during preference check: ${errorMessage}`,
				});
				const errorEvent = new ErrorEvent('error', { message: errorMessage, error: e });
				requestWrapper.dispatchEvent(errorEvent);
			});
		return requestWrapper as IDBOpenDBRequest;
	};


